import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteCmsBrand, getCmsBrand, getCmsBrands } from '../../api/client';
import { getBrandStyle } from '../../utils/catalog';
import BrandModal from './BrandModal';
import DataTablePagination from '../common/DataTablePagination';
import SearchableSelect from '../SearchableSelect';

const COUNTRY_NAME_MAP = {
  CN: 'China',
  JP: 'Japan',
  KR: 'South Korea',
  PK: 'Pakistan',
  US: 'United States',
  DE: 'Germany',
};

const BrandManagement = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [sortOption, setSortOption] = useState('name_asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [editingBrand, setEditingBrand] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadBrands = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load all brands from server to enable seamless client-side search, sort, and pagination
      const data = await getCmsBrands();
      setBrands(data);
    } catch (err) {
      console.error(err);
      setError('Could not load brands list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  const handleOpenBrand = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const detail = await getCmsBrand(id);
      setSelectedBrand(detail);
    } catch (err) {
      console.error(err);
      setError('Could not load brand details.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Synchronize route brandId with selectedBrand
  useEffect(() => {
    if (brandId) {
      handleOpenBrand(brandId);
    } else {
      setSelectedBrand(null);
    }
  }, [brandId, handleOpenBrand]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toastMsg) return;
    const timer = setTimeout(() => setToastMsg(''), 4000);
    return () => clearTimeout(timer);
  }, [toastMsg]);

  const handleDeleteBrand = async (brand) => {
    const ok = window.confirm(
      `Delete brand "${brand.name}"?\n\nA brand that still has vehicles cannot be deleted: delete or move them first.`
    );
    if (!ok) return;
    try {
      await deleteCmsBrand(brand.id);
      setToastMsg('Brand deleted.');
      if (selectedBrand && selectedBrand.id === brand.id) {
        setSelectedBrand(null);
        navigate('/dashboard/marketplace/brands');
      }
      loadBrands();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete the brand.');
    }
  };

  const handleBrandSaved = (savedBrand) => {
    loadBrands();
    if (editingBrand && editingBrand.id) {
      // Updated existing brand
      if (selectedBrand && selectedBrand.id === savedBrand.id) {
        handleOpenBrand(savedBrand.id);
      }
      setToastMsg('Brand updated successfully.');
    } else {
      // Created new brand
      setToastMsg('Brand created successfully.');
      navigate(`/dashboard/marketplace/brands/${savedBrand.id}`);
    }
    setEditingBrand(null);
  };

  // Phase 9: Search across brand name, code/slug, origin classification, country name, and country code
  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;

    return brands.filter((b) => {
      const name = (b.name || '').toLowerCase();
      const slug = (b.slug || '').toLowerCase();
      const origin = (b.origin || '').toLowerCase();
      const countryCode = (b.country || '').toLowerCase();
      const countryName = (COUNTRY_NAME_MAP[b.country] || '').toLowerCase();

      return (
        name.includes(q) ||
        slug.includes(q) ||
        origin.includes(q) ||
        countryCode.includes(q) ||
        countryName.includes(q) ||
        (q === 'chinese' && (origin === 'chinese' || countryCode === 'cn')) ||
        (q === 'american' && (countryCode === 'us')) ||
        (q === 'japanese' && (countryCode === 'jp')) ||
        (q === 'korean' && (countryCode === 'kr')) ||
        (q === 'assembled' && origin === 'assembled') ||
        (q === 'imported' && origin === 'imported')
      );
    });
  }, [brands, search]);

  // Phase 9: Sort by brand name and vehicle count
  const sortedBrands = useMemo(() => {
    const list = [...filteredBrands];
    list.sort((a, b) => {
      if (sortOption === 'name_asc') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortOption === 'name_desc') {
        return (b.name || '').localeCompare(a.name || '');
      }
      if (sortOption === 'vehicles_desc') {
        return (b.vehicle_count || 0) - (a.vehicle_count || 0);
      }
      if (sortOption === 'vehicles_asc') {
        return (a.vehicle_count || 0) - (b.vehicle_count || 0);
      }
      return 0;
    });
    return list;
  }, [filteredBrands, sortOption]);

  // Phase 9: Pagination with 10, 20, 50 per page; default 10
  const pagedBrands = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedBrands.slice(start, start + pageSize);
  }, [sortedBrands, currentPage, pageSize]);

  // Reset pagination to page 1 when search or sorting changes
  const handleSearchChange = (val) => {
    setSearch(val);
    setCurrentPage(1);
  };

  const handleSortChange = (val) => {
    setSortOption(val);
    setCurrentPage(1);
  };

  return (
    <div className="cms-container">
      {/* Friendly success toast */}
      {toastMsg && (
        <div className="dash-alert success" style={{ marginBottom: '16px' }}>
          <i className="fas fa-circle-check" /> {toastMsg}
        </div>
      )}

      {/* Top action / search bar */}
      <div className="cms-topbar">
        <div className="cms-search-box">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Search brands by name, code, origin, or country…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button type="button" className="cms-search-clear" onClick={() => handleSearchChange('')}>
              ×
            </button>
          )}
        </div>

        {/* Phase 9: Sort selector */}
        <div className="cms-sort-box" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: '#5e7d92', fontWeight: 600 }}>Sort by:</span>
          <SearchableSelect
            className="cms-sort-select-wrap"
            value={sortOption}
            onChange={(e) => handleSortChange(e.target.value)}
            aria-label="Sort brands"
            placeholder="Sort brands"
            options={[
              { value: 'name_asc', label: 'Brand Name (A–Z)', icon: '🔤' },
              { value: 'name_desc', label: 'Brand Name (Z–A)', icon: '🔤' },
              { value: 'vehicles_desc', label: 'Vehicle Count (High–Low)', icon: '🚗' },
              { value: 'vehicles_asc', label: 'Vehicle Count (Low–High)', icon: '🚗' },
            ]}
          />
        </div>

        <div className="cms-topbar-actions">
          <button type="button" className="btn-primary" onClick={() => setEditingBrand({})}>
            <i className="fas fa-plus" /> New Brand
          </button>
        </div>
      </div>

      {error && (
        <div className="dash-alert" style={{ marginBottom: '16px' }}>
          <i className="fas fa-circle-exclamation" /> {error}
        </div>
      )}

      {selectedBrand ? (
        /* Brand Detail View (Preserved) */
        <div className="cms-detail-view">
          <div className="cms-detail-header">
            <button
              type="button"
              className="btn-back"
              onClick={() => navigate('/dashboard/marketplace/brands')}
            >
              <i className="fas fa-arrow-left" /> Back to all brands
            </button>
            <div className="cms-detail-title-wrap">
              {selectedBrand.logo_url ? (
                <span className="brand-avatar large logo-img-container">
                  <img
                    src={selectedBrand.logo_url}
                    alt={`${selectedBrand.name} logo`}
                    className="brand-logo-img"
                  />
                </span>
              ) : (
                <span
                  className="brand-avatar large"
                  style={{ background: getBrandStyle(selectedBrand.slug).color }}
                >
                  {getBrandStyle(selectedBrand.slug).initial}
                </span>
              )}
              <div>
                <h2>{selectedBrand.name}</h2>
                <div className="cms-meta-tags">
                  <span className="cms-badge slug">Code: {selectedBrand.slug}</span>
                  <span className={`cms-badge origin-${selectedBrand.origin || 'default'}`}>
                    {selectedBrand.origin === 'chinese'
                      ? 'Chinese Brand'
                      : selectedBrand.origin === 'assembled'
                      ? 'Assembled in PK'
                      : selectedBrand.origin || 'Standard'}
                  </span>
                  {selectedBrand.country && (
                    <span className="cms-badge country">
                      <i className="fas fa-globe" /> {selectedBrand.country}
                    </span>
                  )}
                  <span className="cms-badge count">
                    <i className="fas fa-car" /> {selectedBrand.vehicles?.length || 0} models
                  </span>
                </div>
              </div>
            </div>
            <div className="cms-detail-cta">
              <button
                type="button"
                className="btn-edit-brand"
                onClick={() => setEditingBrand(selectedBrand)}
              >
                <i className="fas fa-pen-to-square" /> Edit Brand
              </button>
              <button
                type="button"
                className="btn-delete-brand"
                onClick={() => handleDeleteBrand(selectedBrand)}
              >
                <i className="fas fa-trash-can" /> Delete Brand
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => navigate(`/dashboard/marketplace/vehicles/new?brandId=${selectedBrand.id}`)}
              >
                <i className="fas fa-plus" /> Add Vehicle under {selectedBrand.name}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate(`/dashboard/marketplace/vehicles?brandId=${selectedBrand.id}`)}
              >
                <i className="fas fa-filter" /> View in Vehicle Management
              </button>
            </div>
          </div>

          <div className="cms-section-card">
            <div className="cms-section-head">
              <h3>
                <i className="fas fa-car-side" /> Vehicles under {selectedBrand.name}
              </h3>
              <span className="cms-text-muted">
                {selectedBrand.vehicles?.length || 0} vehicle{selectedBrand.vehicles?.length === 1 ? '' : 's'} registered
              </span>
            </div>

            {detailLoading ? (
              <div className="dash-loading inline">
                <div className="dash-spinner" />
                <p>Loading brand vehicles…</p>
              </div>
            ) : !selectedBrand.vehicles || selectedBrand.vehicles.length === 0 ? (
              <div className="dash-empty" style={{ padding: '36px 16px' }}>
                <div className="dash-empty-icon">
                  <i className="fas fa-car" />
                </div>
                <h3>No vehicles registered yet</h3>
                <p>Add the first vehicle model under {selectedBrand.name} to populate the catalog.</p>
                <button
                  type="button"
                  className="btn-primary"
                  style={{ marginTop: '14px' }}
                  onClick={() => navigate(`/dashboard/marketplace/vehicles/new?brandId=${selectedBrand.id}`)}
                >
                  <i className="fas fa-plus" /> Add First Vehicle
                </button>
              </div>
            ) : (
              <div className="cms-table-wrap">
                <table className="cms-table">
                  <thead>
                    <tr>
                      <th>Vehicle Name</th>
                      <th>Variant / Year</th>
                      <th>Price</th>
                      <th>Powertrain</th>
                      <th>Category</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBrand.vehicles.map((v) => (
                      <tr key={v.id}>
                        <td>
                          <strong>{v.name}</strong>
                        </td>
                        <td>
                          {v.variant || 'Standard'} {v.model_year ? `(${v.model_year})` : ''}
                        </td>
                        <td>
                          <span className="cms-price">{v.price}</span>
                        </td>
                        <td>
                          <span className={`cms-badge powertrain-${v.powertrain || 'petrol'}`}>
                            {v.powertrain || 'petrol'}
                          </span>
                        </td>
                        <td>{v.category || '—'}</td>
                        <td>
                          <div className="cms-table-actions">
                            <button
                              type="button"
                              className="btn-tbl-edit"
                              onClick={() => navigate(`/dashboard/marketplace/vehicles/${v.id}/edit`)}
                            >
                              <i className="fas fa-pen-to-square" /> Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Phase 9: Card Grid with Search, Sort, and Pagination */
        <div className="cms-card-grid-container">
          {loading ? (
            <div className="dash-loading inline" style={{ padding: '48px 0' }}>
              <div className="dash-spinner" />
              <p>Loading catalog brands…</p>
            </div>
          ) : sortedBrands.length === 0 ? (
            <div className="dash-empty" style={{ padding: '48px 16px' }}>
              <div className="dash-empty-icon">
                <i className="fas fa-tags" />
              </div>
              <h3>No matching brands found</h3>
              <p>
                {search
                  ? `No brands matched "${search}". Try searching by another keyword or code.`
                  : 'No automobile brands exist in the database yet.'}
              </p>
              {search ? (
                <button
                  type="button"
                  className="dash-btn-reset-filters"
                  style={{ marginTop: '14px' }}
                  onClick={() => handleSearchChange('')}
                >
                  <i className="fas fa-rotate-left" /> Clear search
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  style={{ marginTop: '14px' }}
                  onClick={() => setEditingBrand({})}
                >
                  <i className="fas fa-plus" /> Add Brand
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Preserved Responsive Card Grid */}
              <div className="cms-brand-grid">
                {pagedBrands.map((b) => {
                  const style = getBrandStyle(b.slug);
                  return (
                    <div key={b.id} className="cms-brand-card">
                      <div className="cms-brand-card-top">
                        {b.logo_url ? (
                          <span className="brand-avatar logo-img-container">
                            <img
                              src={b.logo_url}
                              alt={`${b.name} logo`}
                              className="brand-logo-img"
                            />
                          </span>
                        ) : (
                          <span className="brand-avatar" style={{ background: style.color }}>
                            {style.initial}
                          </span>
                        )}
                        <div className="cms-brand-info">
                          <h3>{b.name}</h3>
                          <span className="cms-brand-slug">code: {b.slug}</span>
                        </div>
                        <span className="cms-count-badge">
                          <i className="fas fa-car" /> {b.vehicle_count}
                        </span>
                        <button
                          type="button"
                          className="btn-delete-icon"
                          title="Delete brand"
                          aria-label={`Delete ${b.name}`}
                          onClick={() => handleDeleteBrand(b)}
                        >
                          <i className="fas fa-trash-can" />
                        </button>
                      </div>

                      <div className="cms-brand-tags">
                        <span className={`cms-origin-tag ${b.origin || 'assembled'}`}>
                          {b.origin === 'chinese' ? 'Chinese Brand' : b.origin === 'assembled' ? 'Assembled PK' : b.origin || 'Standard'}
                        </span>
                        {b.country && <span className="cms-country-tag">{b.country}</span>}
                      </div>

                      <div className="cms-brand-actions">
                        <button
                          type="button"
                          className="btn-open-brand"
                          onClick={() => navigate(`/dashboard/marketplace/brands/${b.id}`)}
                        >
                          <i className="fas fa-folder-open" /> Open ({b.vehicle_count})
                        </button>
                        <button
                          type="button"
                          className="btn-edit-brand"
                          title="Edit brand details and manufacturer logo"
                          onClick={() => setEditingBrand(b)}
                        >
                          <i className="fas fa-pen-to-square" /> Edit Brand
                        </button>
                        <button
                          type="button"
                          className="btn-add-quick"
                          title="Add vehicle under this brand"
                          onClick={() => navigate(`/dashboard/marketplace/vehicles/new?brandId=${b.id}`)}
                        >
                          <i className="fas fa-plus" /> Add Model
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Data Table Pagination for Card Grid */}
              <div style={{ marginTop: '20px', borderRadius: '16px', overflow: 'hidden', border: '1px solid #eef4f9' }}>
                <DataTablePagination
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={sortedBrands.length}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Brand Creation & Editing Modal Dialog */}
      {editingBrand !== null && (
        <BrandModal
          isOpen={true}
          brand={editingBrand.id ? editingBrand : null}
          onClose={() => setEditingBrand(null)}
          onBrandSaved={handleBrandSaved}
        />
      )}
    </div>
  );
};

export default BrandManagement;
